import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enderecoDeArquivo } from '@/shared/api/client';
import {
  generated,
  signatureRequests,
  signatures,
  templates,
  type DeliveryFormat,
  type GeneratedDocument,
  type GeneratedKind,
  type SignerInput,
  type SignerRole,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { SignatureRequestDocumentBlock } from './SignatureRequestBlocks';
import {
  motivoDaTravaDaAssinatura,
  PERMISSAO_VER_PEDIDOS,
} from './signatureRequestLabels';

/**
 * Minuta, CCG e coleta de assinatura de uma operacao.
 *
 * Os tres formam um so caminho: o texto sai de um template versionado, vira
 * documento da operacao, e o documento e o que se assina. Por isso o painel e um
 * so, e nao tres.
 */

const papeis: { value: SignerRole; label: string }[] = [
  { value: 'representante_legal', label: 'Representante legal' },
  { value: 'avalista', label: 'Avalista' },
  { value: 'corretor', label: 'Corretor' },
  { value: 'testemunha', label: 'Testemunha' },
];

const rotuloDoPapel = Object.fromEntries(
  papeis.map((papel) => [papel.value, papel.label]),
) as Record<SignerRole, string>;

const situacaoDoSignatario: Record<
  string,
  { label: string; tone: 'warning' | 'success' | 'danger' }
> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  assinado: { label: 'Assinado', tone: 'success' },
  recusado: { label: 'Recusado', tone: 'danger' },
};

const situacaoDaColeta: Record<
  string,
  { label: string; tone: 'info' | 'success' | 'neutral' }
> = {
  em_coleta: { label: 'Em coleta', tone: 'info' },
  concluido: { label: 'Concluido', tone: 'success' },
  cancelado: { label: 'Cancelado', tone: 'neutral' },
};

const titulos: Record<GeneratedKind, { titulo: string; descricao: string }> = {
  minuta: {
    titulo: 'Minuta',
    descricao: 'Documento contratual gerado a partir do template padrao.',
  },
  ccg: {
    titulo: 'CCG',
    descricao: 'Carta de contragarantia gerada a partir do template padrao.',
  },
  apolice: {
    titulo: 'Apolice',
    descricao: 'Documento da apolice gerado na emissao.',
  },
  laudo_emissao: {
    titulo: 'Laudo de emissao',
    descricao: 'Registro interno da decisao de emissao.',
  },
};

/**
 * Download do documento gerado, em Word e em PDF.
 *
 * O documento de texto nao tem arquivo, e o texto ja aparece em "Ver texto". O PDF
 * so e oferecido quando ele pode sair: ja existe, ou a instalacao tem conversor. O
 * primeiro pedido gera o PDF e o guarda; os seguintes entregam o mesmo arquivo.
 */
export function BaixarDocumento({ documento }: { documento: GeneratedDocument }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const baixar = useMutation({
    mutationFn: (formato: DeliveryFormat) => generated.download(documento.id, formato),
    onSuccess: (arquivo) => {
      // O endereco leva direto ao armazenamento. Abrir em aba nova deixa a operacao
      // como estava: quem baixa quase sempre continua trabalhando nela.
      window.open(enderecoDeArquivo(arquivo.url), '_blank', 'noopener,noreferrer');
      if (arquivo.format === 'pdf' && !documento.pdf_ready) {
        void queryClient.invalidateQueries({ queryKey: ['generated'] });
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (documento.format !== 'docx') return null;

  return (
    <>
      <Button
        variant="secondary"
        loading={baixar.isPending && baixar.variables === 'docx'}
        onClick={() => baixar.mutate('docx')}
      >
        Baixar Word
      </Button>
      {documento.pdf_available ? (
        <Button
          variant="secondary"
          loading={baixar.isPending && baixar.variables === 'pdf'}
          onClick={() => baixar.mutate('pdf')}
        >
          Baixar PDF
        </Button>
      ) : null}
    </>
  );
}

export function GenerationPanel({
  operationId,
  kind,
}: {
  operationId: string;
  kind: GeneratedKind;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [gerando, setGerando] = useState(false);
  const [abrindoColeta, setAbrindoColeta] = useState<GeneratedDocument | null>(null);
  const [vendo, setVendo] = useState<GeneratedDocument | null>(null);

  const podeGerar = can(kind === 'ccg' ? 'ccg.carta.create' : 'minutas.minuta.create');
  const podeAbrirColeta = can('assinaturas.fluxo.create');

  const consulta = useQuery({
    queryKey: ['generated', kind, operationId],
    queryFn: ({ signal }) => {
      const params = { operation_id: operationId, limit: 20 };
      return kind === 'ccg'
        ? generated.listCounterGuarantees(params, signal)
        : generated.listDrafts(params, signal);
    },
  });

  const cancelar = useMutation({
    mutationFn: (id: string) => generated.cancel(id),
    onSuccess: () => {
      toast.success('Documento cancelado.');
      void queryClient.invalidateQueries({ queryKey: ['generated'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const itens = consulta.data?.items ?? [];
  const vigente = itens.find((documento) => documento.status === 'vigente');
  // A coleta cancelada nao conta: depois dela o documento aceita coleta nova (defeito 7
  // dos documentos, anexo C do 47; P-MIN-4).
  const coletaAtiva =
    vigente?.signature_flow !== null &&
    vigente?.signature_flow !== undefined &&
    vigente.signature_flow.status !== 'cancelado';
  const anteriores = itens.filter((documento) => documento.status !== 'vigente');
  const rotulo = titulos[kind];

  // O pedido de assinatura pendente ou assinado sobre o documento vigente trava gerar de
  // novo e cancelar: a mesma consulta do bloco de assinatura (ADR 0079, R5-A03).
  const pedidosDoVigente = useQuery({
    queryKey: ['signature-requests', 'documento', vigente?.id],
    queryFn: ({ signal }) =>
      signatureRequests.listByDocument(vigente?.id ?? '', signal),
    enabled:
      can(PERMISSAO_VER_PEDIDOS) && vigente !== undefined && vigente.format === 'docx',
  });
  const travaDaAssinatura = vigente
    ? motivoDaTravaDaAssinatura(pedidosDoVigente.data?.[0]?.status)
    : undefined;

  return (
    <Card>
      <CardHeader
        title={rotulo.titulo}
        description={rotulo.descricao}
        action={
          podeGerar ? (
            <Button
              variant="secondary"
              disabled={travaDaAssinatura !== undefined}
              title={travaDaAssinatura}
              onClick={() => setGerando(true)}
            >
              {vigente ? 'Gerar de novo' : 'Gerar'}
            </Button>
          ) : null
        }
      />

      <CardBody className="flex flex-col gap-3">
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar">
            {consulta.error.message}
          </Alert>
        ) : null}

        {consulta.isPending ? (
          <p className="text-sm text-ink-500">Carregando.</p>
        ) : !vigente ? (
          <EmptyState
            icon={kind === 'ccg' ? 'stamp' : 'file'}
            title={`Nenhuma ${rotulo.titulo.toLowerCase()} gerada`}
            description="O texto sai da versao publicada de um template. Cadastre o modelo em Templates e gere por aqui."
          />
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-ink-900">
                  {rotulo.titulo} {vigente.number}
                </p>
                <p className="text-2xs text-ink-500">
                  Template versao {vigente.template_version} · {vigente.generated_label}{' '}
                  em {formatDateTime(vigente.generated_at)}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setVendo(vigente)}>
                  Ver texto
                </Button>
                <BaixarDocumento documento={vigente} />
                {podeAbrirColeta && !coletaAtiva ? (
                  <Button onClick={() => setAbrindoColeta(vigente)}>
                    Abrir assinaturas
                  </Button>
                ) : null}
                {podeGerar && !coletaAtiva ? (
                  <Button
                    variant="danger"
                    loading={cancelar.isPending}
                    disabled={travaDaAssinatura !== undefined}
                    title={travaDaAssinatura}
                    onClick={() => cancelar.mutate(vigente.id)}
                  >
                    Cancelar
                  </Button>
                ) : null}
              </div>
            </div>

            {coletaAtiva && vigente.signature_flow ? (
              <SignatureFlowBlock flowId={vigente.signature_flow.id} />
            ) : null}

            {/* Assinatura eletronica pelo provedor (P-MIN-4): so o documento em Word. */}
            {vigente.format === 'docx' ? (
              <SignatureRequestDocumentBlock
                documentId={vigente.id}
                subject={`${rotulo.titulo} ${vigente.number}`}
              />
            ) : null}
          </div>
        )}

        {anteriores.length > 0 ? (
          <details className="rounded-md border border-line px-3 py-2">
            <summary className="cursor-pointer text-xs font-semibold text-ink-700">
              Geracoes anteriores ({anteriores.length})
            </summary>
            <ul className="mt-2 flex flex-col divide-y divide-line">
              {anteriores.map((documento) => (
                <li
                  key={documento.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <span className="text-xs text-ink-600">
                    {rotulo.titulo} {documento.number} · versao{' '}
                    {documento.template_version} ·{' '}
                    {documento.status === 'substituida' ? 'substituida' : 'cancelada'}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={() => setVendo(documento)}>
                      Ver texto
                    </Button>
                    <BaixarDocumento documento={documento} />
                  </div>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </CardBody>

      {gerando ? (
        <GenerateDialog
          open
          operationId={operationId}
          kind={kind}
          onClose={() => setGerando(false)}
        />
      ) : null}

      {abrindoColeta ? (
        <OpenFlowDialog
          open
          document={abrindoColeta}
          onClose={() => setAbrindoColeta(null)}
        />
      ) : null}

      {vendo ? (
        <Modal
          open
          onClose={() => setVendo(null)}
          title={`${rotulo.titulo} ${vendo.number}`}
          description={`Texto gerado a partir da versao ${vendo.template_version} do template.`}
          size="lg"
          footer={<Button onClick={() => setVendo(null)}>Fechar</Button>}
        >
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded bg-ink-50 p-4 text-sm text-ink-800">
            {vendo.body}
          </pre>
        </Modal>
      ) : null}
    </Card>
  );
}

function GenerateDialog({
  operationId,
  kind,
  open,
  onClose,
}: {
  operationId: string;
  kind: GeneratedKind;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [escolhido, setTemplateId] = useState('');

  const consulta = useQuery({
    queryKey: ['templates', { kind, status: 'ativo' }],
    queryFn: ({ signal }) =>
      templates.list({ kind, status: 'ativo', limit: 100 }, signal),
  });

  // A minuta da cotacao vinculada a modalidade (P-MIN-5) ja vem escolhida; quem gera
  // pode trocar. Sem vinculo, a resposta e 404 e nada vem escolhido.
  const vinculada = useQuery({
    queryKey: ['draft-template', operationId],
    queryFn: ({ signal }) => generated.boundDraftTemplate(operationId, signal),
    enabled: kind === 'minuta',
    retry: false,
  });
  const templateId = escolhido || (vinculada.data?.id ?? '');

  const gerar = useMutation({
    mutationFn: () =>
      kind === 'ccg'
        ? generated.generateCounterGuarantee(operationId, templateId)
        : generated.generateDraft(operationId, templateId),
    onSuccess: () => {
      toast.success('Documento gerado.');
      void queryClient.invalidateQueries({ queryKey: ['generated'] });
      void queryClient.invalidateQueries({ queryKey: ['operation'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Template sem versao publicada nao gera nada, e por isso nao aparece na lista:
  // oferecer o que o servidor recusaria seria convidar ao erro.
  const disponiveis = (consulta.data?.items ?? []).filter(
    (template) => template.current_version > 0,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Gerar ${titulos[kind].titulo.toLowerCase()}`}
      description="O texto sai da versao publicada do template e fica gravado como esta."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={gerar.isPending}
            disabled={templateId === ''}
            onClick={() => gerar.mutate()}
          >
            Gerar
          </Button>
        </>
      }
    >
      {consulta.isPending ? (
        <p className="text-sm text-ink-500">Carregando os templates.</p>
      ) : disponiveis.length === 0 ? (
        <Alert tone="info" title="Nenhum template publicado deste tipo">
          Cadastre o modelo em Templates e publique a primeira versao. Sem versao
          publicada o template nao gera documento.
        </Alert>
      ) : (
        <div className="flex flex-col gap-2">
          <Select
            label="Template"
            placeholder="Escolha o modelo"
            value={templateId}
            options={disponiveis.map((template) => ({
              value: template.id,
              label: `${template.name} (versao ${template.current_version})`,
            }))}
            onChange={(event) => setTemplateId(event.target.value)}
          />
          {vinculada.data && templateId === vinculada.data.id ? (
            <p className="text-2xs text-ink-500">
              Minuta vinculada a modalidade da operacao.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function OpenFlowDialog({
  document,
  open,
  onClose,
}: {
  document: GeneratedDocument;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [notas, setNotas] = useState('');
  const [lista, setLista] = useState<SignerInput[]>([
    { name: '', email: '', role: 'representante_legal' },
  ]);

  const abrir = useMutation({
    mutationFn: () =>
      signatures.open(
        document.id,
        notas,
        lista.filter((signatario) => signatario.name.trim() !== ''),
      ),
    onSuccess: () => {
      toast.success('Coleta de assinatura aberta.');
      void queryClient.invalidateQueries({ queryKey: ['generated'] });
      void queryClient.invalidateQueries({ queryKey: ['signature-flows'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function atualizar(indice: number, campo: keyof SignerInput, valor: string) {
    setLista((atual) =>
      atual.map((signatario, posicao) =>
        posicao === indice ? { ...signatario, [campo]: valor } : signatario,
      ),
    );
  }

  const validos = lista.filter((signatario) => signatario.name.trim() !== '');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Abrir coleta de assinatura"
      description="A plataforma registra a assinatura colhida fora dela, com quem afirmou e quando. Nenhum provedor de assinatura eletronica esta integrado."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={abrir.isPending}
            disabled={validos.length === 0}
            onClick={() => abrir.mutate()}
          >
            Abrir coleta
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {lista.map((signatario, indice) => (
          <div key={indice} className="grid gap-3 sm:grid-cols-3">
            <Field
              label={`Nome do signatario ${indice + 1}`}
              value={signatario.name}
              onChange={(event) => atualizar(indice, 'name', event.target.value)}
            />
            <Field
              label={`E-mail do signatario ${indice + 1}`}
              type="email"
              value={signatario.email ?? ''}
              onChange={(event) => atualizar(indice, 'email', event.target.value)}
            />
            <Select
              label={`Papel do signatario ${indice + 1}`}
              options={papeis}
              value={signatario.role}
              onChange={(event) => atualizar(indice, 'role', event.target.value)}
            />
          </div>
        ))}

        <div>
          <Button
            variant="secondary"
            onClick={() =>
              setLista((atual) => [...atual, { name: '', email: '', role: 'avalista' }])
            }
          >
            Acrescentar signatario
          </Button>
        </div>

        <Textarea
          label="Observacoes da coleta"
          value={notas}
          onChange={(event) => setNotas(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/** Coleta de assinatura, com os signatarios e o que falta. */
export function SignatureFlowBlock({ flowId }: { flowId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [recusando, setRecusando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');

  const podeRegistrar = can('assinaturas.fluxo.update');

  const consulta = useQuery({
    queryKey: ['signature-flow', flowId],
    queryFn: ({ signal }) => signatures.get(flowId, signal),
  });

  const invalidar = () => {
    void queryClient.invalidateQueries({ queryKey: ['signature-flow', flowId] });
    void queryClient.invalidateQueries({ queryKey: ['generated'] });
    void queryClient.invalidateQueries({ queryKey: ['signature-flows'] });
  };

  const registrar = useMutation({
    mutationFn: ({
      signerId,
      signed,
      note,
    }: {
      signerId: string;
      signed: boolean;
      note: string;
    }) => signatures.record(flowId, signerId, signed, note),
    onSuccess: () => {
      toast.success('Registro feito.');
      setRecusando(null);
      setMotivo('');
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const encerrar = useMutation({
    mutationFn: () => signatures.complete(flowId),
    onSuccess: () => {
      toast.success('Coleta encerrada.');
      invalidar();
      void queryClient.invalidateQueries({ queryKey: ['operation'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const cancelar = useMutation({
    mutationFn: () => signatures.cancel(flowId),
    onSuccess: () => {
      toast.success('Coleta cancelada.');
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (consulta.isPending) {
    return <p className="text-sm text-ink-500">Carregando a coleta.</p>;
  }
  if (consulta.isError || !consulta.data) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar a coleta">
        {consulta.error?.message ?? 'Erro desconhecido.'}
      </Alert>
    );
  }

  const coleta = consulta.data;
  const situacao = situacaoDaColeta[coleta.status] ?? {
    label: coleta.status,
    tone: 'neutral' as const,
  };
  const todosAssinaram =
    coleta.signers.length > 0 &&
    coleta.signers.every((signatario) => signatario.status === 'assinado');

  return (
    <div className="rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Assinaturas
          </p>
          <Badge tone={situacao.tone}>{situacao.label}</Badge>
        </div>
        <span className="text-2xs text-ink-500">
          Registro interno: assinatura colhida fora da plataforma
        </span>
      </div>

      <ul className="mt-2 flex flex-col divide-y divide-line">
        {coleta.signers.map((signatario) => {
          const estado = situacaoDoSignatario[signatario.status] ?? {
            label: signatario.status,
            tone: 'warning' as const,
          };
          return (
            <li
              key={signatario.id}
              className="flex flex-wrap items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink-900">{signatario.name}</p>
                <p className="text-2xs text-ink-500">
                  {rotuloDoPapel[signatario.role] ?? signatario.role}
                  {signatario.recorded_label
                    ? ` · registrado por ${signatario.recorded_label}`
                    : ''}
                  {signatario.signed_at
                    ? ` em ${formatDateTime(signatario.signed_at)}`
                    : ''}
                </p>
                {signatario.note ? (
                  <p className="text-2xs text-ink-600">{signatario.note}</p>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <Badge tone={estado.tone}>{estado.label}</Badge>
                {podeRegistrar &&
                signatario.status === 'pendente' &&
                coleta.status === 'em_coleta' ? (
                  <>
                    <Button
                      variant="secondary"
                      loading={registrar.isPending}
                      onClick={() =>
                        registrar.mutate({
                          signerId: signatario.id,
                          signed: true,
                          note: '',
                        })
                      }
                    >
                      Registrar assinatura
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => setRecusando(signatario.id)}
                    >
                      Registrar recusa
                    </Button>
                  </>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {podeRegistrar && coleta.status === 'em_coleta' ? (
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            loading={cancelar.isPending}
            onClick={() => cancelar.mutate()}
          >
            Cancelar coleta
          </Button>
          <Button
            loading={encerrar.isPending}
            disabled={!todosAssinaram}
            onClick={() => encerrar.mutate()}
          >
            Encerrar coleta
          </Button>
        </div>
      ) : null}

      {recusando ? (
        <Modal
          open
          onClose={() => setRecusando(null)}
          title="Registrar recusa"
          description="A recusa exige motivo: sem ele nao se decide se cabe insistir, trocar o signatario ou encerrar."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setRecusando(null)}>
                Voltar
              </Button>
              <Button
                variant="danger"
                loading={registrar.isPending}
                disabled={motivo.trim() === ''}
                onClick={() =>
                  registrar.mutate({
                    signerId: recusando,
                    signed: false,
                    note: motivo,
                  })
                }
              >
                Registrar recusa
              </Button>
            </>
          }
        >
          <Textarea
            label="Motivo da recusa"
            required
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </Modal>
      ) : null}
    </div>
  );
}
